import axios, { type AxiosError } from 'axios'
import { env } from '@/shared/config/env'
import i18n from '@/shared/i18n'
import { getAnalyticsAnonymousId, getAnalyticsSessionId } from '@/shared/analytics/analytics-ids'

export interface ApiErrorPayload {
  error?: string
  Error?: string
  message?: string
  [key: string]: unknown
}

export class ApiError extends Error {
  public readonly status?: number
  public readonly payload?: ApiErrorPayload

  constructor(message: string, options?: { status?: number; payload?: ApiErrorPayload }) {
    super(message)
    this.name = 'ApiError'
    this.status = options?.status
    this.payload = options?.payload
  }
}

export const httpClient = axios.create({
  baseURL: env.apiBaseUrl,
  withCredentials: true,
})

let currentAuthToken: string | null = null

export function setAuthToken(token: string | null) {
  currentAuthToken = token

  if (token) {
    httpClient.defaults.headers.common.Authorization = `Bearer ${token}`
  } else {
    delete httpClient.defaults.headers.common.Authorization
  }
}

export function getAuthToken() {
  return currentAuthToken
}

function normalizeLanguageHeader(language?: string | null) {
  return language?.trim().toLowerCase().startsWith('ru') ? 'ru' : 'en'
}

httpClient.interceptors.request.use((config) => {
  config.headers = config.headers ?? {}
  config.headers['X-Language'] = normalizeLanguageHeader(i18n.resolvedLanguage ?? i18n.language)
  const anonymousId = getAnalyticsAnonymousId()
  const sessionId = getAnalyticsSessionId()
  if (anonymousId) {
    config.headers['X-Analytics-Anonymous-Id'] = anonymousId
  }
  if (sessionId) {
    config.headers['X-Analytics-Session-Id'] = sessionId
  }
  return config
})

function normalizeError(error: AxiosError<ApiErrorPayload>): ApiError {
  const status = error.response?.status
  const rawPayload = error.response?.data
  const payload: ApiErrorPayload | undefined = rawPayload ?? undefined

  const messageCandidates: (string | undefined)[] = [
    payload?.error,
    payload?.Error,
    payload?.message,
    error.response?.statusText,
    error.message,
  ]

  const messageFromPayload =
    messageCandidates.find((candidate) => typeof candidate === 'string' && candidate.trim().length > 0)?.trim() ??
    i18n.t('errors.api_generic')

  return new ApiError(messageFromPayload, { status, payload })
}

httpClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiErrorPayload>) => {
    if (error.isAxiosError) {
      return Promise.reject(normalizeError(error))
    }

    return Promise.reject(error)
  },
)
